import i18n from '../i18n';
import { getCurrencyConfig } from './currencyConfig';

/**
 * Formats a number as currency with locale-aware thousand separators
 * Uses the current i18next language to determine currency and formatting
 * 
 * @param {number|string} value - The value to format
 * @param {Object} options - Optional configuration
 * @param {string} options.language - Override language (defaults to current i18n language)
 * @param {boolean} options.showSymbol - Whether to show currency symbol (default: true)
 * @returns {string} Formatted currency string
 */
const formatCurrency = (value, options = {}) => {
  const { language = i18n.language, showSymbol = true } = options;
  const config = getCurrencyConfig(language);
  
  const numValue = typeof value === 'string' ? parseFloat(value) : value;
  
  if (isNaN(numValue)) {
    return showSymbol ? `0.00 ${config.symbol}` : '0.00';
  }

  // Format with locale-specific thousand separators and 2 decimal places
  const formatted = numValue.toLocaleString(config.locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (!showSymbol) {
    return formatted;
  }

  // Position symbol based on locale convention
  if (config.position === 'before') {
    return `${config.symbol}${formatted}`;
  }
  return `${formatted} ${config.symbol}`;
};

/**
 * Get just the currency symbol for the current language
 * @param {string} language - Optional language override
 * @returns {string} Currency symbol
 */
export const getCurrencySymbol = (language = i18n.language) => {
  const config = getCurrencyConfig(language);
  return config.symbol;
};

/**
 * Get the currency code for the current language
 * @param {string} language - Optional language override
 * @returns {string} Currency code (EUR, RON, BGN)
 */
export const getCurrencyCode = (language = i18n.language) => {
  const config = getCurrencyConfig(language);
  return config.code;
};

export default formatCurrency;
